/**
 * useActivityTracker — SS-229 T10/T12/T13
 *
 * Singleton hook mounted once in App.tsx. Captures user activity events
 * and flushes them to Supabase `scout_activity_log` in batches.
 *
 * Events captured:
 * - session_start / session_end / session_pause / session_resume
 * - page_view / page_leave
 * - click (on [data-track] elements)
 * - form_start / form_submit / form_abandon (T12)
 * - friction_rage_click / friction_dead_click (T13)
 *
 * Types live in `./useActivityTracker.types`, pure helpers in
 * `./useActivityTracker.helpers`, and the HTTP transport in
 * `./useActivityTracker.flusher`. This file is just the React hook —
 * lifecycle, state and the DOM event handlers.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getBuildHash, getSWStatus, setScoutLogSessionId, logScoutEvent } from '@appic/lib/utils/scoutLog'
import { getBuildIdentity } from '@appic/lib/utils/buildIdentity'
import { getLastSyncTimestamp } from '@appic/lib'
import type {
  ActivityEvent,
  ActivityTrackerAPI,
  ClickRecord,
  FormTrackingEntry,
} from './useActivityTracker.types'
import {
  FLUSH_INTERVAL_MS,
  RAGE_CLICK_THRESHOLD,
  RAGE_CLICK_WINDOW_MS,
  compactSelector,
  generateUUID,
  getSupabaseSession,
  isInteractive,
} from './useActivityTracker.helpers'
import { createFlusher } from './useActivityTracker.flusher'

export type { ActivityTrackerAPI } from './useActivityTracker.types'

export function useActivityTracker(): ActivityTrackerAPI {
  const location = useLocation()

  // Stable refs that persist across renders.
  const sessionId = useRef(generateUUID())
  const buffer = useRef<ActivityEvent[]>([])
  const mountTime = useRef(Date.now())
  const prevRoute = useRef<string | null>(null)
  const pageEnterTime = useRef(Date.now())
  const flusherRef = useRef(createFlusher())
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const activeForms = useRef(new Map<string, FormTrackingEntry>())
  const recentClicks = useRef<ClickRecord[]>([])
  const pauseStart = useRef<number | null>(null)

  const pushEvent = useCallback(
    (event: string, payload: Record<string, unknown> = {}, route?: string | null) => {
      const session = getSupabaseSession()
      if (!session) return // Not authenticated — skip
      buffer.current.push({
        user_id: session.userId,
        session_id: sessionId.current,
        event,
        payload,
        route: route !== undefined ? route : location.pathname,
        created_at: new Date().toISOString(),
      })
    },
    [location.pathname],
  )

  const doFlush = useCallback((beacon = false) => {
    if (buffer.current.length === 0) return
    const events = buffer.current.splice(0)
    if (beacon) {
      flusherRef.current.flushBeacon(events)
    } else {
      flusherRef.current.flush(events)
    }
  }, [])

  const trackFormStart = useCallback((formId: string) => {
    if (activeForms.current.has(formId)) return // Already tracking
    activeForms.current.set(formId, { startedAt: Date.now(), fieldCount: 0 })
    pushEvent('form_start', { form_id: formId })
  }, [pushEvent])

  const trackFormSubmit = useCallback((formId: string, fieldCount?: number) => {
    const entry = activeForms.current.get(formId)
    const durationMs = entry ? Date.now() - entry.startedAt : 0
    pushEvent('form_submit', {
      form_id: formId,
      duration_ms: durationMs,
      field_count: fieldCount ?? entry?.fieldCount ?? 0,
    })
    activeForms.current.delete(formId)
  }, [pushEvent])

  // Mount-only effect: session_start, lifecycle listeners, periodic flush.
  useEffect(() => {
    setScoutLogSessionId(sessionId.current)

    pushEvent('session_start', {
      user_agent: navigator.userAgent,
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
      referrer: document.referrer || null,
      build_hash: getBuildHash(),
    })

    // Async session-diagnostics — runs once IDB + SW are ready. Failures
    // here must not block the app, but we surface them via console.warn
    // so corrupted IDB / SW state isn't masked silently (D-33).
    void (async () => {
      try {
        const email = localStorage.getItem('appic:currentUserEmail')
        const [swInfo, lastSync] = await Promise.all([
          getSWStatus(),
          email ? getLastSyncTimestamp(email) : Promise.resolve(undefined),
        ])
        const cacheAgeMs = lastSync ? Date.now() - new Date(lastSync).getTime() : null
        // BUILD IDENTITY (app_build / build_time / build_identity_resolved):
        // `build_hash` alone could never answer "did this phone get the new
        // build?" — a git SHA does not change when the SAME commit is rebuilt
        // into a new image, and in production it was the literal 'dev' anyway.
        // `__APP_BUILD__` (the Cloud Build $SHORT_SHA-$BUILD_ID) has been
        // plumbed into the bundle since 2026-06-01 and was simply never sent
        // anywhere. Emitting it is what makes the deployed-version question
        // answerable AT THE SUBSTRATE, per device, without asking the rep.
        // `build_identity_resolved: false` keeps the ABSENCE loud and queryable
        // rather than letting an unidentifiable bundle look identified (D-33 R1).
        const build = getBuildIdentity()
        logScoutEvent('session_diagnostics', {
          build_hash: build.commit,
          app_build: build.build,
          build_time: build.builtAt,
          build_identity_resolved: build.resolved,
          sw_state: swInfo.state,
          sw_script: swInfo.scriptURL,
          sw_waiting: swInfo.waiting,
          last_sync_at: lastSync ?? null,
          cache_age_ms: cacheAgeMs,
          online: navigator.onLine,
          email: email ?? null,
        })
      } catch (err) {
        console.warn('[activity-tracker] session diagnostics failed', err)
      }
    })()

    flushTimerRef.current = setInterval(() => doFlush(), FLUSH_INTERVAL_MS)

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        pauseStart.current = Date.now()
        pushEvent('session_pause', { duration_ms: Date.now() - mountTime.current })
        doFlush(true) // flush before tab hides
      } else {
        const pauseDuration = pauseStart.current ? Date.now() - pauseStart.current : 0
        pauseStart.current = null
        pushEvent('session_resume', { pause_duration_ms: pauseDuration })
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    function onBeforeUnload() {
      pushEvent('session_end', { duration_ms: Date.now() - mountTime.current })
      pushEvent('page_leave', {
        route: location.pathname,
        duration_ms: Date.now() - pageEnterTime.current,
      })
      activeForms.current.forEach((entry, formId) => {
        pushEvent('form_abandon', {
          form_id: formId,
          duration_ms: Date.now() - entry.startedAt,
          fields_filled: entry.fieldCount,
        })
      })
      activeForms.current.clear()
      doFlush(true)
    }
    window.addEventListener('beforeunload', onBeforeUnload)

    function onClick(e: MouseEvent) {
      const target = e.target as Element | null
      if (!target) return

      const now = Date.now()
      const sel = compactSelector(target)

      // Rage-click detection (T13): N clicks on the same selector
      // within RAGE_CLICK_WINDOW_MS counts as user frustration.
      let record = recentClicks.current.find((r) => r.selector === sel)
      if (!record) {
        record = { selector: sel, timestamps: [] }
        recentClicks.current.push(record)
      }
      record.timestamps.push(now)
      record.timestamps = record.timestamps.filter((t) => now - t < RAGE_CLICK_WINDOW_MS)
      if (record.timestamps.length >= RAGE_CLICK_THRESHOLD) {
        pushEvent('friction_rage_click', {
          selector: sel,
          click_count: record.timestamps.length,
          route: location.pathname,
        })
        record.timestamps = [] // Reset after detecting
      }

      // Dead-click detection (T13): a click on a non-interactive element.
      if (!isInteractive(target)) {
        pushEvent('friction_dead_click', { selector: sel, route: location.pathname })
      }

      // Explicit data-track click — instrumented buttons/links.
      const tracked = target.closest('[data-track]')
      if (tracked) {
        const action = tracked.getAttribute('data-track') || ''
        const text = (tracked.textContent || '').trim().slice(0, 80)
        pushEvent('click', { action, element: compactSelector(tracked), text })
      }
    }
    document.addEventListener('click', onClick, true) // capture phase

    // Prune stale rage-click records so the array doesn't grow unbounded.
    const pruneTimer = setInterval(() => {
      const now = Date.now()
      recentClicks.current = recentClicks.current.filter((r) =>
        r.timestamps.some((t) => now - t < RAGE_CLICK_WINDOW_MS * 2),
      )
    }, 5_000)

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current)
      clearInterval(pruneTimer)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClick, true)
      doFlush() // Final flush on unmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, [])

  // Route-change tracking: emit page_leave for the previous route and
  // page_view for the new one; abandon any in-flight forms.
  useEffect(() => {
    const currentRoute = location.pathname
    const prev = prevRoute.current

    if (prev !== null && prev !== currentRoute) {
      pushEvent('page_leave', {
        route: prev,
        duration_ms: Date.now() - pageEnterTime.current,
      }, prev)

      activeForms.current.forEach((entry, formId) => {
        pushEvent('form_abandon', {
          form_id: formId,
          duration_ms: Date.now() - entry.startedAt,
          fields_filled: entry.fieldCount,
        }, prev)
      })
      activeForms.current.clear()

      pushEvent('page_view', { from_route: prev, to_route: currentRoute }, currentRoute)
    } else if (prev === null) {
      // Initial page view
      pushEvent('page_view', { from_route: null, to_route: currentRoute }, currentRoute)
    }

    prevRoute.current = currentRoute
    pageEnterTime.current = Date.now()
  }, [location.pathname, pushEvent])

  return {
    trackFormStart,
    trackFormSubmit,
    flushNow: () => doFlush(),
  }
}
